import FlagIcon from '@mui/icons-material/Flag';
import OutlinedFlagIcon from '@mui/icons-material/OutlinedFlag';
import FormControl from '@mui/material/FormControl';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { useTheme } from '@mui/material/styles';
import classNames from 'classnames';
// Base
import type { CSSProperties, ChangeEvent, ReactElement, SyntheticEvent } from 'react';
import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import { makeStyles } from 'tss-react/mui';

// CUSTOM
import {
  getEmptyFlagIconColor,
  getFlagFieldOptions,
  getFlagIconColor,
  type UserFlagWithColor,
} from 'data/transactions/config';

import { TextField } from '@mui/material';
import type { QcsColor } from 'app-components/flux/transactions/transactionsTypes';
import { buttonFocusVisibleStyles } from 'themes/muiComponents/buttonFocusTokens';
import RegisterButton from '../RegisterButton';

const useStyles = makeStyles()((theme) => ({
  flagRoot: {
    minWidth: 160,
  },
  flagBase: {
    height: 56,
    margin: 0,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.border.light}`,
    borderRadius: 12,
    padding: '12px 16px',
    '&:hover': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focusVisible': buttonFocusVisibleStyles(theme),
  },
  activeMenu: {
    borderColor: theme.palette.primary.main,
  },
  // Matches FastActionsRow's reviewedButtonActive treatment (green fill + border).
  highlighted: {
    backgroundColor: theme.palette.success.light,
    borderColor: theme.palette.success.main,
  },
}));

// @ts-expect-error FIXME TS7006
const stopProp = (e) => e.stopPropagation();

const generateMenuItems = (
  flagOptions: UserFlagWithColor[],
  onClick?: (value: QcsColor | null | undefined) => void,
): ReactElement[] =>
  flagOptions.map((item) => (
    <MenuItem
      key={`flagmenu:${item.label}`}
      value={item.value}
      id={`flag-color-${item.value}`}
      onClick={onClick ? () => onClick(item.value) : undefined}
    >
      {item.value === 'NONE' ? (
        'None'
      ) : (
        <ListItemIcon>
          <FlagIcon
            style={{
              fontSize: 20,
              color: item.color,
            }}
          />
        </ListItemIcon>
      )}
    </MenuItem>
  ));

function FlagField({
  value,
  onChange,
  className,
  label = 'Follow-up flag',
  margin = 'normal',
  plain,
  paddedFlagButton,
  focusable,
  disabled,
  highlighted,
  ...otherProps
}: {
  value: QcsColor | null | undefined;
  onChange: (val: QcsColor | null | undefined) => void;
  className?: string;
  label?: string;
  margin?: 'none' | 'normal' | 'dense' | undefined;
  plain?: boolean;
  paddedFlagButton?: boolean;
  /** Adds the button to the tab order. Off by default: register rows and grid cells drive their own keyboard nav. */
  focusable?: boolean;
  disabled?: boolean;
  /** Highlights the button, e.g. right after an AI suggestion updates it. */
  highlighted?: boolean;
  style?: CSSProperties;
  onKeyDown?: () => undefined;
}) {
  const { classes, cx } = useStyles();
  const theme = useTheme();
  const flagOptions = useMemo(() => getFlagFieldOptions(theme), [theme]);
  const fieldMenuItems = useMemo(() => generateMenuItems(flagOptions), [flagOptions]);

  const divRef = useRef<HTMLDivElement>(null);

  const [menuAnchor, setMenuAnchor] = useState<HTMLDivElement | null>(null);

  // INTERNAL FUNCTIONS
  const buttonClick = useCallback((e: SyntheticEvent) => {
    e.stopPropagation();
    setMenuAnchor(divRef.current || null);
  }, []);
  const closeMenu = useCallback(() => setMenuAnchor(null), []);

  const onItemClick = (val: QcsColor | null | undefined) => {
    onChange(val);
    closeMenu();
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(e?.target?.value as QcsColor | null | undefined);
  };

  const iconColor = value && value !== 'NONE' ? getFlagIconColor(theme, value) : getEmptyFlagIconColor(theme);

  return plain ? (
    <div role='presentation' onClick={stopProp} onKeyDown={stopProp} {...otherProps} ref={divRef} className={className}>
      <RegisterButton
        // @ts-expect-error FIXME TS2322
        Icon={value && value !== 'NONE' ? FlagIcon : OutlinedFlagIcon}
        iconColor={iconColor}
        onClick={buttonClick}
        small
        id='flag-field'
        className={classNames(
          paddedFlagButton ? classes.flagBase : '',
          menuAnchor ? classes.activeMenu : '',
          highlighted ? classes.highlighted : '',
        )}
        disabled={disabled}
        tabIndex={focusable ? 0 : undefined}
        aria-label={label}
        aria-haspopup='menu'
        aria-expanded={Boolean(menuAnchor)}
      />
      {menuAnchor && (
        <Menu id='IgnoreMenu-menu' anchorEl={menuAnchor} keepMounted open onClose={closeMenu}>
          {generateMenuItems(flagOptions, onItemClick)}
        </Menu>
      )}
    </div>
  ) : (
    <FormControl variant='filled' margin={margin} className={cx(classes.flagRoot, className)}>
      <TextField
        select
        id='flag-field'
        value={value || 'NONE'}
        onChange={handleChange}
        label={label}
        disabled={disabled}
        {...otherProps}
      >
        {fieldMenuItems}
      </TextField>
    </FormControl>
  );
}

export default memo(FlagField);
